import { useState } from "react";
import { useLang } from "../lib/useLang";
import { useLocation } from "../state/LocationContext";
import { LocationSheet } from "../components/LocationSheet";
import { MenuCatalog } from "../components/MenuCatalog";
import { SmartImage } from "../components/SmartImage";
import { DealPopup } from "../components/DealPopup";
import { ReviewsSection, Stars } from "../components/ReviewsSection";
import { HERO_IMAGE } from "../data/images";
import { ratingSummary } from "../data/reviews";

function Mandala() {
  return (
    <svg
      className="pointer-events-none absolute -end-8 -top-8 h-48 w-48 text-gold opacity-[0.12]"
      viewBox="0 0 100 100"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      aria-hidden
    >
      <circle cx="50" cy="50" r="46" />
      <circle cx="50" cy="50" r="34" />
      <circle cx="50" cy="50" r="22" />
      {Array.from({ length: 12 }).map((_, i) => {
        const a = (i * Math.PI) / 6;
        return (
          <line
            key={i}
            x1={50}
            y1={50}
            x2={50 + 46 * Math.cos(a)}
            y2={50 + 46 * Math.sin(a)}
          />
        );
      })}
    </svg>
  );
}

export default function HomePage() {
  const { t, pick } = useLang();
  const { branch, area, mode } = useLocation();
  const [showLoc, setShowLoc] = useState(false);

  const hasLoc = Boolean(branch && mode);
  const label =
    mode === "area" && area
      ? pick(area.en, area.ar)
      : branch
        ? pick(branch.name, branch.nameAr)
        : "";

  return (
    <div className="space-y-4">
      <DealPopup />
      {/* Hero */}
      <section className="fade-up relative overflow-hidden rounded-3xl text-white shadow-raised">
        <SmartImage
          src={HERO_IMAGE}
          alt=""
          eager
          className="absolute inset-0 h-full w-full object-cover"
          fallback={<div className="absolute inset-0 bg-gradient-to-br from-brand to-brand-dark" />}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-brand/95 via-brand/85 to-brand-dark/80" />
        <Mandala />
        <div className="relative flex items-center gap-4 px-5 py-6 sm:px-8 sm:py-10">
          <img
            src="/logo.svg"
            alt="Khan Baba"
            className="h-14 w-14 shrink-0 rounded-full ring-2 ring-gold/60 sm:h-20 sm:w-20"
          />
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-gold">
              {t("app.tagline")}
            </p>
            <h1 className="display mt-1 max-w-md text-2xl leading-tight sm:text-3xl">
              {t("home.heroTitle")}
            </h1>
            <p className="mt-1.5 max-w-md text-sm text-white/85">
              {t("home.heroSubtitle")}
            </p>
            <div className="mt-2 flex items-center gap-1.5">
              <Stars value={Math.round(ratingSummary().avg)} />
              <span className="text-sm font-bold">{ratingSummary().avg.toFixed(1)}</span>
              <span className="text-xs text-white/70">
                · {t("reviews.count", { count: ratingSummary().count })}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Location bar */}
      <button
        onClick={() => setShowLoc((s) => !s)}
        className="card flex w-full items-center justify-between p-3.5 text-start"
      >
        <span className="flex items-center gap-2 text-sm">
          <span>📍</span>
          <span className="text-black/50">{t("location.deliverTo")}:</span>
          <span className="font-semibold text-ink">
            {hasLoc ? label : t("location.chooseBranch")}
          </span>
        </span>
        <span className="text-sm font-semibold text-brand">{t("location.change")}</span>
      </button>
      {(showLoc || !hasLoc) && <LocationSheet onDone={() => setShowLoc(false)} />}

      {/* Fast-delivery callout */}
      <div className="flex items-center gap-3 rounded-2xl bg-brand/5 p-3.5 ring-1 ring-brand/15">
        <span className="text-2xl">⚡</span>
        <div>
          <div className="font-bold text-brand">{t("home.fastTitle")}</div>
          <p className="text-sm text-black/60">{t("home.fastNote")}</p>
        </div>
      </div>

      {/* The menu, right on the homepage */}
      <MenuCatalog />

      {/* Reviews */}
      <ReviewsSection />
    </div>
  );
}
